import React, { useState, useEffect, useRef } from 'react'
import '../../../assets/styles/_beveledmenu.scss'
import Menu from './Menu'
import Option from './Option'
import OverlayMenuModal from '../../molecules/OverlayModal/OverlayModal'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { hasUrlFeatureFlag } from '../../molecules/URLFeatureFlag/URLFeatureFlag'
import { Text, ToggleButton } from '../../atoms'
import { useAuthContext } from '../../../contexts/AuthContext'
import { useAuth } from '../../../hooks/useAuth'
import { useNavigate } from 'react-router'
import { logger } from '../../../utils'
import { ControlHighlight, KeyboardControl } from '../../molecules/KeyboardControls'
import { HighlightEffect } from '../../../enums/KeyboardControls'
import returnDashboardIcon from '../../../assets/images/return-dashboard-icon.svg'
import logoutIcon from '../../../assets/images/buttons/LOG-OUT-icon.svg'
import { useBackgroundMusic } from '../../../contexts/BackgroundMusicContext'
import { useUserPreferences } from '../../../contexts/UserPreferencesContext'
import ProgressiveImage from '../../molecules/ProgressiveImage/ProgressiveImage'

interface StudentMenuProps {
  className?: string
}

enum MenuType {
  HELP = 'help',
  ACCESSIBILITY = 'accessibility'
}

enum AsmMode {
  VISION = 'vision',
  HEARING = 'hearing',
  MOTOR = 'motor',
  COGNITIVE = 'cognitive'
}

export enum FocusableElement {
  LinkHelp = 'link-help',
  LinkAccessibility = 'link-accessibility',
  ButtonLogout = 'button-logout'
}

const kbControlsLayer = 'student-menu'

const idToggleButtonA11yEad = 'student-menu-toggle-a11y-ead'
const idToggleButtonA11yMcq = 'student-menu-toggle-a11y-mcq'
const idToggleButtonA11yReset = 'student-menu-toggle-a11y-reset'
const idToggleButtonA11yBgm = 'student-menu-toggle-a11y-bgm'
const idToggleButtonA11yCc = 'student-menu-toggle-a11y-cc'
const idToggleButtonA11yReduceMotion = 'student-menu-toggle-a11y-reduce-motion'

const labelEad = 'Extended Audio Descriptions'
const labelMcq = 'Screen Reader Questions - Text Only'
const labelResetToDefault = 'Reset to Default'
const labelBgm = 'Background Music'
const labelCc = 'Closed Captions'
const labelReduceMotion = 'Reduce Motion'

// ASM UI-only: local state for Accessibility Modes
const labelAsmVision = 'Vision Support'
const labelAsmHearing = 'Hearing Support'
const labelAsmMotor = 'Motor Support'
const labelAsmCognitive = 'Cognitive Support'

const StudentMenu: React.FC<StudentMenuProps> = ({ className }) => {
  const { setIsPrefMute } = useBackgroundMusic()
  const [activeMenu, setActiveMenu] = useState<MenuType | null>(null)
  const [isSetup, setIsSetup] = useState<boolean>(false)
  const keyboardControls = useKeyboardControls()
  const userPreferences = useUserPreferences()
  const urlFeatureFlagEnabled = hasUrlFeatureFlag()
  const { signout } = useAuthContext()
  const { setUserProfile } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (isSetup) {
      setIsPrefMute(!userPreferences.preferenceA11yBgm)
    }
  }, [userPreferences.preferenceA11yBgm, setIsPrefMute, isSetup])

  useEffect(() => {
    const handleCaptionsChange = (e: Event) => {
      const enabled = (e as CustomEvent).detail
      userPreferences.setPreferenceA11yCc(Boolean(enabled))
    }
    
    window.addEventListener('captionsChanged', handleCaptionsChange as EventListener)
    return () => window.removeEventListener('captionsChanged', handleCaptionsChange as EventListener)
  }, [userPreferences])

  // Set setup status when preferences are loaded
  useEffect(() => {
    setIsSetup(userPreferences.isPreferencesLoaded)
  }, [userPreferences.isPreferencesLoaded])

  // Manage keyboard controls layer
  useEffect(() => {
    if (activeMenu) {
      keyboardControls.pushActiveLayer(kbControlsLayer)
    } else {
      keyboardControls.resetDefaultLayer()
    }

    return () => {
      keyboardControls.resetDefaultLayer()
    }
  }, [keyboardControls, activeMenu])

  // Update a11y reset toggle status
  useEffect(() => {
    if (!isSetup) { return }

    if (userPreferences.preferenceA11yEad || userPreferences.preferenceA11yMcq || userPreferences.preferenceA11yBgm || userPreferences.preferenceA11yCc || userPreferences.preferenceA11yReduceMotion) {
      userPreferences.setPreferenceA11yReset(false)
    }
  }, [userPreferences, isSetup])

  useEffect(() => {
    if (!isSetup) return
    window.dispatchEvent(new CustomEvent('captionsChanged', { detail: userPreferences.preferenceA11yCc }))
  }, [userPreferences.preferenceA11yCc, isSetup])


  // Notify Unity of reduce motion changes in real-time
  useEffect(() => {
    // Notify Unity of the change in real-time
    const w = window as any
    logger.log(`🎯 StudentMenu: Reduce motion changed to: ${String(userPreferences.preferenceA11yReduceMotion)}`)
    logger.log(`🎯 StudentMenu: Unity_UpdateAccessibility function exists: ${String(typeof w.Unity_UpdateAccessibility === 'function')}`)

    if (typeof w.Unity_UpdateAccessibility === 'function') {
      logger.log(`🎯 StudentMenu: Calling Unity_UpdateAccessibility with: ${String(userPreferences.preferenceA11yReduceMotion)}`)
      w.Unity_UpdateAccessibility(userPreferences.preferenceA11yReduceMotion)
    } else {
      logger.log('❌ StudentMenu: Unity_UpdateAccessibility function not available')
    }
  }, [userPreferences.preferenceA11yReduceMotion])

  // Reset a11y preferences
  useEffect(() => {
    if (userPreferences.preferenceA11yReset) {
      userPreferences.setPreferenceA11yEad(false)
      userPreferences.setPreferenceA11yMcq(false)
      userPreferences.setPreferenceA11yBgm(true)
      userPreferences.setPreferenceA11yCc(false)
      userPreferences.setPreferenceA11yReduceMotion(false)
    }
  }, [userPreferences.preferenceA11yReset, userPreferences])

  // ASM UI-only toggle handlers and labels

  // Generic function to handle ASM mode toggles
  const toggleAsmMode = (modeName: AsmMode, currentValue: boolean, setter: (value: boolean) => void) => {
    manuallyToggledModesRef.current = new Set(manuallyToggledModesRef.current).add(modeName)
    
    if (!currentValue) {
      // If turning ON this mode, turn OFF all other modes (mutually exclusive)
      if (modeName !== AsmMode.VISION && userPreferences.preferenceAsmVision) userPreferences.setPreferenceAsmVision(false)
      if (modeName !== AsmMode.HEARING && userPreferences.preferenceAsmHearing) userPreferences.setPreferenceAsmHearing(false)
      if (modeName !== AsmMode.MOTOR && userPreferences.preferenceAsmMotor) userPreferences.setPreferenceAsmMotor(false)
      if (modeName !== AsmMode.COGNITIVE && userPreferences.preferenceAsmCognitive) userPreferences.setPreferenceAsmCognitive(false)
    }
    
    setter(!currentValue)
  }

  function toggleAsmVision (): void { toggleAsmMode(AsmMode.VISION, userPreferences.preferenceAsmVision, userPreferences.setPreferenceAsmVision) }
  function toggleAsmHearing (): void { toggleAsmMode(AsmMode.HEARING, userPreferences.preferenceAsmHearing, userPreferences.setPreferenceAsmHearing) }
  function toggleAsmMotor (): void { toggleAsmMode(AsmMode.MOTOR, userPreferences.preferenceAsmMotor, userPreferences.setPreferenceAsmMotor) }
  function toggleAsmCognitive (): void { toggleAsmMode(AsmMode.COGNITIVE, userPreferences.preferenceAsmCognitive, userPreferences.setPreferenceAsmCognitive) }

  // Function to reset all ASM modes
  function resetAllAsmModes (): void {
    if (userPreferences.preferenceAsmVision) userPreferences.setPreferenceAsmVision(false)
    if (userPreferences.preferenceAsmHearing) userPreferences.setPreferenceAsmHearing(false)
    if (userPreferences.preferenceAsmMotor) userPreferences.setPreferenceAsmMotor(false)
    if (userPreferences.preferenceAsmCognitive) userPreferences.setPreferenceAsmCognitive(false)
  }

  // Track which ASM modes were manually toggled (vs auto-toggled due to individual setting change)
  const manuallyToggledModesRef = useRef<Set<string>>(new Set())

  // Generic effect to handle ASM mode changes
  useEffect(() => {
    if (!isSetup) return

    // ASM Mode configurations - defines what each mode does when activated
    const asmModeConfigs = {
      [AsmMode.COGNITIVE]: {
        isActive: userPreferences.preferenceAsmCognitive,
        onActivate: () => {
          userPreferences.setPreferenceA11yCc(true)        // Closed Captions ON
          userPreferences.setPreferenceA11yEad(true)       // Extended Audio Descriptions ON
          userPreferences.setPreferenceA11yMcq(true)       // Screen Reader Questions ON
          userPreferences.setPreferenceA11yReduceMotion(true) // Reduce Motion ON
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
          userPreferences.setPreferenceA11yReset(false)    // Default Settings OFF
        },
        onDeactivate: () => {
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
        }
      },
      [AsmMode.VISION]: {
        isActive: userPreferences.preferenceAsmVision,
        onActivate: () => {
          userPreferences.setPreferenceA11yBgm(true)       // Background Music ON
          userPreferences.setPreferenceA11yEad(true)       // Extended Audio Descriptions ON
          userPreferences.setPreferenceA11yMcq(true)       // Screen Reader Questions ON
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
          userPreferences.setPreferenceA11yReset(false)    // Default Settings OFF
        },
        onDeactivate: () => {
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
        }
      },
      [AsmMode.HEARING]: {
        isActive: userPreferences.preferenceAsmHearing,
        onActivate: () => {
          userPreferences.setPreferenceA11yCc(true)        // Closed Captions ON
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
          userPreferences.setPreferenceA11yReset(false)    // Default Settings OFF
        },
        onDeactivate: () => {
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
        }
      },
      [AsmMode.MOTOR]: {
        isActive: userPreferences.preferenceAsmMotor,
        onActivate: () => {
          userPreferences.setPreferenceA11yBgm(true)       // Background Music ON
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
          userPreferences.setPreferenceA11yReset(false)    // Default Settings OFF
        },
        onDeactivate: () => {
          userPreferences.setPreferenceA11yBgm(false)      // Background Music OFF
          userPreferences.setPreferenceA11yCc(false)       // Closed Captions OFF
          userPreferences.setPreferenceA11yEad(false)      // Extended Audio Descriptions OFF
          userPreferences.setPreferenceA11yMcq(false)      // Screen Reader Questions OFF
          userPreferences.setPreferenceA11yReduceMotion(false) // Reduce Motion OFF
        }
      }
    }

    Object.entries(asmModeConfigs).forEach(([modeName, config]) => {
      const wasManuallyToggled = manuallyToggledModesRef.current.has(modeName)
      
      if (config.isActive) {
        // When mode is turned ON, activate its settings
        config.onActivate()
      } else if (wasManuallyToggled) {
        // Only deactivate settings if mode was manually toggled OFF
        // (not when it was auto-turned off due to individual setting change)
        config.onDeactivate()
      }
    })
    
    // Clear the manually toggled flags after processing
    if (manuallyToggledModesRef.current.size > 0) {
      manuallyToggledModesRef.current = new Set()
    }
  }, [userPreferences, isSetup])

  // Modified toggle functions to turn off all ASM modes when individual settings change
  function togglePreferenceA11yEad (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yEad(!userPreferences.preferenceA11yEad)
  }

  function togglePreferenceA11yMcq (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yMcq(!userPreferences.preferenceA11yMcq)
  }

  function togglePreferenceA11yReset (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yReset(!userPreferences.preferenceA11yReset)
  }

  function togglePreferenceA11yBgm (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yBgm(!userPreferences.preferenceA11yBgm)
  }

  function togglePreferenceA11yCc (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yCc(!userPreferences.preferenceA11yCc)
  }

  function togglePreferenceA11yReduceMotion (): void {
    resetAllAsmModes()
    userPreferences.setPreferenceA11yReduceMotion(!userPreferences.preferenceA11yReduceMotion)
  }

  const handleSignOut = (): void => {
    signout()
      .then(() => {
        setUserProfile(null)
        navigate('/')
      })
      .catch((error) => logger.error(error))
  }

  const openMenu = (menuType: MenuType): void => {
    setActiveMenu(menuType)
  }

  const closeMenu = (): void => {
    setActiveMenu(null)
  }

  const renderModalContent = (title: string, children: React.ReactNode): JSX.Element => (
    <div className='w-full h-full flex flex-col'>
      <div className='mb-[36px] shrink-0'>
        <h2
          className='text-6xl font-bold text-dopage-accessibilityHighlight m-0 leading-none'
        >
          {title}
        </h2>
      </div>
      <div className='flex-1 min-h-0'>
        {children}
      </div>
    </div>
  )

  return (
    <div>
      <div data-testid='student-menu' className={`w-full pr-28 ${className ?? ''}`}>
        <Menu>
          {urlFeatureFlagEnabled && (
            <Option
              text='Help'
              focusableElement={FocusableElement.LinkHelp}
              onClick={() => openMenu(MenuType.HELP)}
            />
          )}
          <Option
            text='Accessibility'
            focusableElement={FocusableElement.LinkAccessibility}
            onClick={() => openMenu(MenuType.ACCESSIBILITY)}
          />
        </Menu>
      </div>

      {activeMenu === MenuType.ACCESSIBILITY && (
        <OverlayMenuModal
          onClick={closeMenu}
          overlayWidth='70vw'
          overlayHeight='80vh'
          kbControlsLayer={kbControlsLayer}
          showOverlay
          innerBgClassName='bg-dopage-charcoalGray'
        >
          {renderModalContent('ACCESSIBILITY SETTINGS',
            <div className='flex gap-[40px] h-full'>
              {/* Left Box - Accessible Modes */}
              <div className='flex-1 beveled-corner bg-neutral-090'>
                <div className='inner bg-neutral-090 h-full p-[24px] flex flex-col'>
                  <h3 id='a11y-modes-heading' className='text-dopage-tealGray font-bold text-lg mb-[24px] pl-[16px]'>
                    ACCESSIBLE MODES
                  </h3>
                  <section
                    aria-labelledby='a11y-modes-heading'
                    className='flex-1 min-h-0 overflow-y-auto mb-[16px] px-[16px]'
                  >
                    <div className='h-[2px] bg-dopage-charcoalGray w-full' />
                    <ul className='m-0 p-0 list-none'>
                      <li className='py-[14px]'>
                        <ToggleButton
                          id='student-menu-toggle-a11y-mode-vision'
                          kbControlsLayer={kbControlsLayer}
                          label={labelAsmVision}
                          isOn={userPreferences.preferenceAsmVision}
                          onClick={toggleAsmVision}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelAsmVision}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />
                      <li className='py-[14px]'>
                        <ToggleButton
                          id='student-menu-toggle-a11y-mode-hearing'
                          kbControlsLayer={kbControlsLayer}
                          label={labelAsmHearing}
                          isOn={userPreferences.preferenceAsmHearing}
                          onClick={toggleAsmHearing}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelAsmHearing}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />
                      <li className='py-[14px]'>
                        <ToggleButton
                          id='student-menu-toggle-a11y-mode-motor'
                          kbControlsLayer={kbControlsLayer}
                          label={labelAsmMotor}
                          isOn={userPreferences.preferenceAsmMotor}
                          onClick={toggleAsmMotor}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelAsmMotor}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />
                      <li className='py-[14px]'>
                        <ToggleButton
                          id='student-menu-toggle-a11y-mode-cognitive'
                          kbControlsLayer={kbControlsLayer}
                          label={labelAsmCognitive}
                          isOn={userPreferences.preferenceAsmCognitive}
                          onClick={toggleAsmCognitive}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelAsmCognitive}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                    </ul>
                    <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />
                  </section>
                  {/* Return to Dashboard Button */}
                  <div className='mt-auto w-full'>
                    <div className='h-[2px] bg-dopage-charcoalGray w-full mb-[2px]' />
                    <ControlHighlight
                      active={keyboardControls.isEnabled && keyboardControls.isElementFocused('return-dashboard')}
                      highlightEffect={HighlightEffect.ButtonHexOutline}
                    >
                      <KeyboardControl layer={kbControlsLayer}>
                        <button
                          id='return-dashboard'
                          className='h-[32px] flex items-center text-dopage-lightGreen font-bold hover:opacity-80 leading-none pt-[2px]'
                          onClick={closeMenu}
                        >
                          <ProgressiveImage src={returnDashboardIcon} alt='' className='mr-2 h-[25px] w-[25px]' />
                          <Text
                            type='span'
                            text='Return to Dashboard'
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </button>
                      </KeyboardControl>
                    </ControlHighlight>
                  </div>
                </div>
              </div>
              
              {/* Right Box - Individual Settings */}
              <div className='flex-1 beveled-corner bg-neutral-090 relative'>
                <div className='inner bg-neutral-090 h-full p-[24px] flex flex-col min-h-0'>
                  <h3 id='individual-settings-heading' className='text-dopage-tealGray font-bold text-lg mb-[24px] pl-[16px]'>
                    INDIVIDUAL SETTINGS
                  </h3>
                  
                  <section
                    aria-labelledby='individual-settings-heading'
                    className='flex-1 min-h-0 overflow-y-auto mb-[16px] px-[16px]'
                  >
                    {/* Top separator */}
                    <div className='h-[2px] bg-dopage-charcoalGray w-full' />

                    <ul className='m-0 p-0 list-none'>
                      {/* Background Music */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yBgm}
                          kbControlsLayer={kbControlsLayer}
                          label={labelBgm}
                          isOn={userPreferences.preferenceA11yBgm}
                          onClick={() => togglePreferenceA11yBgm()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelBgm}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />

                      {/* Closed Captions */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yCc}
                          kbControlsLayer={kbControlsLayer}
                          label={labelCc}
                          isOn={userPreferences.preferenceA11yCc}
                          onClick={() => togglePreferenceA11yCc()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelCc}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />

                      {/* Extended Audio Descriptions */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yEad}
                          kbControlsLayer={kbControlsLayer}
                          label={labelEad}
                          isOn={userPreferences.preferenceA11yEad}
                          onClick={() => togglePreferenceA11yEad()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelEad}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />

                      {/* Screen Reader Questions */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yMcq}
                          kbControlsLayer={kbControlsLayer}
                          label={labelMcq}
                          isOn={userPreferences.preferenceA11yMcq}
                          onClick={() => togglePreferenceA11yMcq()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelMcq}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />

                      {/* Reduce Motion */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yReduceMotion}
                          kbControlsLayer={kbControlsLayer}
                          label={labelReduceMotion}
                          isOn={userPreferences.preferenceA11yReduceMotion}
                          onClick={() => togglePreferenceA11yReduceMotion()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelReduceMotion}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                      <div className='h-[2px] bg-dopage-charcoalGray w-full -mt-[2px]' />

                      {/* Default Settings */}
                      <li className='py-[14px]'>
                        <ToggleButton
                          id={idToggleButtonA11yReset}
                          kbControlsLayer={kbControlsLayer}
                          label={labelResetToDefault}
                          isOn={userPreferences.preferenceA11yReset}
                          onClick={() => togglePreferenceA11yReset()}
                          variant='accessibility'
                          ariaLive='off'
                        >
                          <Text
                            type='span'
                            text={labelResetToDefault}
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </ToggleButton>
                      </li>
                    </ul>

                    <div className='h-[2px] bg-dopage-charcoalGray w-full' />
                  </section>
                  {/* LOG OUT Button */}
                  <div className='mt-auto w-full'>
                    <div className='h-[2px] bg-dopage-charcoalGray w-full mb-[2px]' />
                    <ControlHighlight
                      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonLogout)}
                      highlightEffect={HighlightEffect.ButtonHexOutline}
                    >
                      <KeyboardControl layer={kbControlsLayer}>
                        <button
                          id={FocusableElement.ButtonLogout}
                          data-testid='highlight-button-logout'
                          className='h-[32px] flex items-center hover:opacity-80 leading-none pt-[2px]'
                          onClick={handleSignOut}
                        >
                          <ProgressiveImage src={logoutIcon} alt='' className='mr-2 h-[25px] w-[25px]' />
                          <Text
                            type='span'
                            text='LOG OUT'
                            size='SM'
                            bold
                            className='text-dopage-lightGreen'
                          />
                        </button>
                      </KeyboardControl>
                    </ControlHighlight>
                  </div>
                </div>
              </div>
            </div>
          )}
        </OverlayMenuModal>
      )}

      {activeMenu === MenuType.HELP && (
        <OverlayMenuModal
          onClick={closeMenu}
          overlayWidth='70vw'
          overlayHeight='80vh'
          kbControlsLayer={kbControlsLayer}
          showOverlay
        >
          {renderModalContent('Help Options',
            <p /* Content */ />
          )}
        </OverlayMenuModal>
      )}
    </div>
  )
}

export default StudentMenu
